import { useCallback, useMemo, useState } from "react";
import ExecutivePriorityDomainsModal from "@/components/ExecutivePriorityDomainsModal";
import "@/components/ExecutivePriorityDomainsModal.css";
import HeroStatusChip from "@/components/HeroStatusChip";
import { BlockLink } from "@/components/DrilldownLink";
import { splitIntoInsightBuckets } from "@/components/AiInsightsCard";
import { useMistralFirstInsights } from "@/hooks/useMistralFirstInsights";
import { parseInsightBullets } from "@/utils/parseInsightBullets";
import {
  buildExecutiveOverviewMarkdown,
  formatOshNarrative,
  normalizeOshTerminology,
} from "@/utils/executiveOverviewDerived";
import { resolveAiText } from "@/utils/resolveAiGuidance";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);

const formatStudents = (n) => {
  if (typeof n !== "number" || n <= 0) return "0";
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  return fmt(n);
};

const dotClass = (index) => {
  if (index >= 85) return "";
  if (index >= 70) return "amber";
  return "red";
};

const tagClass = (status) => {
  if (status === "Green") return "green";
  if (status === "Red") return "red";
  return "";
};

const buildNarrative = (shi, domains) => {
  const status = shi?.rag_status?.status ?? "Amber";
  const statusLower = status.toLowerCase();
  const teacher = domains.teacher?.index ?? 0;
  const identity = domains.identity?.index ?? 0;
  const infra = domains.infrastructure?.index ?? 0;

  if (teacher < 50 && identity >= 80 && infra >= 80) {
    return `The district is in ${statusLower} status. Infrastructure and identity are strong, while teacher quality remains the highest-impact improvement area.`;
  }
  if (status === "Green") {
    return "The district is performing well across core domains with sustained improvement momentum.";
  }
  if (status === "Red") {
    return "The district needs urgent intervention across multiple domains to restore school health performance.";
  }
  return formatOshNarrative(shi);
};

const getPrimaryAction = (domains) => {
  const entries = [
    { key: "teacher", label: "teacher quality", index: domains.teacher?.index ?? 0 },
    { key: "operational", label: "operational certification", index: domains.operational?.index ?? 0 },
    { key: "identity", label: "student identity", index: domains.identity?.index ?? 0 },
    { key: "infrastructure", label: "infrastructure", index: domains.infrastructure?.index ?? 0 },
  ];
  const lowest = [...entries].sort((a, b) => a.index - b.index)[0];
  if (lowest.key === "teacher") {
    return "Start a teacher-quality improvement sprint for low CTET completion blocks before the next district review.";
  }
  if (lowest.key === "operational") {
    return "Accelerate data certification and completion workflows in blocks with the lowest operational index.";
  }
  if (lowest.key === "identity") {
    return "Close APAAR and Aadhaar identity gaps in schools where enrolment is high but compliance lags.";
  }
  return "Prioritize infrastructure remediation in blocks with the weakest classroom and toilet readiness scores.";
};

const getLowestDomainKey = (domains) => {
  const entries = [
    { key: "teacher", index: domains.teacher?.index ?? 0 },
    { key: "operational", index: domains.operational?.index ?? 0 },
    { key: "identity", index: domains.identity?.index ?? 0 },
    { key: "infrastructure", index: domains.infrastructure?.index ?? 0 },
  ];
  return [...entries].sort((a, b) => a.index - b.index)[0]?.key ?? "teacher";
};

const buildActionQueue = (domains, blockRankings) => {
  const priorityBlocks = [...(blockRankings || [])]
    .filter((b) => b.rag?.status !== "Green")
    .sort((a, b) => (a.shi_score ?? 0) - (b.shi_score ?? 0))
    .slice(0, 5)
    .map((b) => b.block_name)
    .filter(Boolean);
  const blockText = priorityBlocks.length ? priorityBlocks.join(", ") : "priority blocks";

  const actions = [
    {
      title: "Launch teacher-quality improvement sprint",
      description: `Prioritize low CTET completion schools in blocks needing intervention (${blockText}).`,
      iconVariant: "purple",
      icon: "graduation",
    },
    {
      title: "Close identity data gaps",
      description: "Improve APAAR completion where Aadhaar is already strong but student identity is incomplete.",
      iconVariant: "",
      icon: "shield",
    },
  ];

  const completion = domains.operational?.completion_rate ?? 0;
  const certification = domains.operational?.certification_rate ?? 0;
  if (completion >= 90 && certification < 50) {
    actions.push({
      title: "Review certification bottleneck",
      description: `Data completion is ${completion}% but certification is only ${certification}% — schedule officer-level follow-up in ${blockText}.`,
      iconVariant: "orange",
      icon: "chart",
    });
  }

  return actions;
};

const radarPoint = (score, axis) => {
  const pct = Math.min(Math.max(score / 100, 0), 1);
  const positions = {
    top: [0, -130 * pct],
    right: [130 * pct, 0],
    bottom: [0, 130 * pct],
    left: [-130 * pct, 0],
  };
  const [x, y] = positions[axis];
  return `${x},${y}`;
};

const IconShield = () => (
  <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const IconBuilding = () => (
  <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 21h18" />
    <path d="M5 21V7l8-4v18" />
    <path d="M19 21V11l-6-4" />
  </svg>
);

const IconGraduation = () => (
  <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m22 10-10 5-10-5 10-5 10 5Z" />
    <path d="M6 12v5c3 2 9 2 12 0v-5" />
  </svg>
);

const IconOperational = () => (
  <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5Z" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87" />
  </svg>
);

const IconChart = () => (
  <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18" />
    <path d="m19 9-5 5-4-4-3 3" />
  </svg>
);

const actionIcon = (type) => {
  if (type === "graduation") return <IconGraduation />;
  if (type === "chart") return <IconChart />;
  return <IconShield />;
};

const ExecutiveOverviewTab = ({
  shiSummary,
  effectiveOverview,
  safeShi,
  onDomainClick,
  onScrollToRankings,
  scopeLabel = "District scope",
}) => {
  const [priorityDomainsOpen, setPriorityDomainsOpen] = useState(false);
  const shiValue = shiSummary.school_health_index ?? 0;
  const shiColor = shiSummary.rag_status?.color ?? "#f59e0b";
  const ragStatus = shiSummary.rag_status?.status ?? "Amber";

  const domains = effectiveOverview?.domain_summary ?? {};
  const identity = domains.identity ?? {};
  const infrastructure = domains.infrastructure ?? {};
  const teacher = domains.teacher ?? {};
  const operational = domains.operational ?? {};

  const breakdown = safeShi?.shi_breakdown ?? [];
  const maxContribution = 25;
  const blockRankings = safeShi?.block_rankings ?? [];

  const scopeKey = useMemo(
    () => JSON.stringify({ shi: shiValue, rag: ragStatus, blocks: blockRankings.length }),
    [shiValue, ragStatus, blockRankings.length]
  );

  const buildLocalMarkdown = useCallback(
    () =>
      buildExecutiveOverviewMarkdown({
        shiSummary,
        domains,
        blockRankings,
      }),
    [shiSummary, domains, blockRankings]
  );

  const { markdown: aiMarkdown, loading: aiLoading, isMistral } = useMistralFirstInsights({
    kind: "executive-overview",
    title: "Executive Overview",
    buildLocalMarkdown,
    context: { shiSummary, domains, blockRankings },
    enabled: shiValue > 0,
    scopeKey,
  });

  const buckets = useMemo(() => splitIntoInsightBuckets(aiMarkdown), [aiMarkdown]);
  const aiNarrative = normalizeOshTerminology(parseInsightBullets(buckets.insights)[0]);
  const narrative =
    normalizeOshTerminology(
      resolveAiText(aiLoading, aiNarrative, buildNarrative(shiSummary, domains))
    ) ?? "Generating AI insights…";
  const primaryAction =
    resolveAiText(aiLoading, parseInsightBullets(buckets.recs)[0], getPrimaryAction(domains)) ??
    "Generating AI insights…";

  const localActions = buildActionQueue(domains, blockRankings);
  const aiActionBullets = parseInsightBullets(buckets.actions);
  const actions =
    !aiLoading && aiActionBullets.length >= 2
      ? aiActionBullets.slice(0, 3).map((text, i) => {
          const title = text.split(/[:.]/)[0].slice(0, 55) || `Action ${i + 1}`;
          return {
            title: title.length > 50 ? `${title.slice(0, 47)}…` : title,
            description: text,
            iconVariant: i === 2 ? "orange" : i === 0 ? "purple" : "",
            icon: i === 0 ? "graduation" : i === 2 ? "chart" : "shield",
          };
        })
      : aiLoading
        ? [{ title: "Generating AI insights…", description: "", iconVariant: "", icon: "chart" }]
        : localActions;

  const lowestDomain = getLowestDomainKey(domains);

  const handleDomainKeyDown = (event, tabId) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onDomainClick?.(tabId);
    }
  };

  const radarScores = {
    identity: identity.index ?? 0,
    infrastructure: infrastructure.index ?? 0,
    teacher: teacher.index ?? 0,
    operational: operational.index ?? 0,
  };

  const radarPolygon = [
    radarPoint(radarScores.identity, "top"),
    radarPoint(radarScores.infrastructure, "right"),
    radarPoint(radarScores.teacher, "bottom"),
    radarPoint(radarScores.operational, "left"),
  ].join(" ");

  return (
    <div className="ed-content">
      <section className="ed-hero-card">
        <div className="ed-hero-layout">
          <div>
            <div className="ed-health-main">
              <div
                className="ed-gauge"
                aria-label={`School Overview ${shiValue} percent`}
                style={{
                  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${shiColor} 0 ${shiValue}%, rgba(245,158,11,.14) ${shiValue}% 100%)`,
                  boxShadow: `inset 0 0 0 1px var(--ed-border), 0 20px 42px ${shiColor}22`,
                }}
              >
                <div>
                  <strong style={{ color: shiColor }}>{shiValue}%</strong>
                  <span>OSH</span>
                </div>
              </div>
              <div className="ed-health-copy">
                <h2 className="dashboard-kpi-hero-title">School Overview</h2>
                <p>{narrative}</p>
                <div className="ed-status-row">
                  <HeroStatusChip
                    chipClass="ed-status-chip"
                    variant={tagClass(ragStatus) || "amber"}
                    onClick={ragStatus !== "Green" ? () => setPriorityDomainsOpen(true) : undefined}
                    ariaLabel={
                      ragStatus !== "Green"
                        ? `${ragStatus} status — view priority domains and at-risk blocks`
                        : undefined
                    }
                  >
                    {ragStatus} Status
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="ed-status-chip"
                    variant="blue"
                    onClick={() => setPriorityDomainsOpen(true)}
                    ariaLabel="View 4 priority domains — problems, at-risk blocks and resolutions"
                  >
                    4 Priority Domains
                  </HeroStatusChip>
                  <HeroStatusChip chipClass="ed-status-chip" variant={isMistral ? "purple" : "blue"}>
                    {aiLoading ? "Generating…" : isMistral ? "Mistral AI" : "Rule-based"}
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="ed-summary-note">
              <div className="ed-icon-box purple">
                <IconGraduation />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="ed-metric-grid" aria-label="Executive KPIs">
            <article className="ed-metric-card">
              <small>Schools</small>
              <strong>{fmt(effectiveOverview?.quick_stats?.total_schools)}</strong>
              <em>Across selected scope</em>
            </article>
            <article className="ed-metric-card">
              <small>Students</small>
              <strong>{formatStudents(effectiveOverview?.quick_stats?.total_students)}</strong>
              <em>Enrolment base</em>
            </article>
            <article className="ed-metric-card">
              <small>Teachers</small>
              <strong>{fmt(effectiveOverview?.quick_stats?.total_teachers)}</strong>
              <em>Deployment strength</em>
            </article>
            <article className="ed-metric-card">
              <small>Classrooms</small>
              <strong>{fmt(effectiveOverview?.quick_stats?.total_classrooms)}</strong>
              <em>Physical capacity</em>
            </article>
          </div>
        </div>
      </section>

      <div className="ed-section-title">
        <div>
          <h2>Domain Snapshot</h2>
          <p>Four domain cards with clearer status dots and reduced micro-text for a more readable executive view.</p>
        </div>
        <button type="button" className="ed-section-action" onClick={() => onDomainClick?.(lowestDomain)}>
          View diagnostics →
        </button>
      </div>

      <section className="ed-domain-grid">
        <article className="ed-domain-card" onClick={() => onDomainClick?.("identity")} onKeyDown={(e) => handleDomainKeyDown(e, "identity")} role="button" tabIndex={0}>
          <div className="ed-domain-head">
            <div className="ed-icon-box"><IconShield /></div>
            <span className={`ed-status-dot ${dotClass(identity.index)}`} />
          </div>
          <h3>Student Overview</h3>
          <div className="ed-domain-score">
            <strong>{identity.index ?? 0}</strong>
            <span>Domain Health</span>
          </div>
          <div className="ed-progress-line"><i style={{ width: `${identity.index ?? 0}%` }} /></div>
          <div className="ed-domain-meta">
            <span>Aadhaar {identity.aadhaar_pct ?? 0}%</span>
            <span>APAAR {identity.apaar_pct ?? 0}%</span>
          </div>
        </article>

        <article className="ed-domain-card" onClick={() => onDomainClick?.("infrastructure")} onKeyDown={(e) => handleDomainKeyDown(e, "infrastructure")} role="button" tabIndex={0}>
          <div className="ed-domain-head">
            <div className="ed-icon-box green"><IconBuilding /></div>
            <span className={`ed-status-dot ${dotClass(infrastructure.index)}`} />
          </div>
          <h3>Infrastructure Overview</h3>
          <div className="ed-domain-score">
            <strong>{infrastructure.index ?? 0}</strong>
            <span>Domain Health</span>
          </div>
          <div className="ed-progress-line">
            <i style={{ width: `${infrastructure.index ?? 0}%`, background: "var(--ed-accent)" }} />
          </div>
          <div className="ed-domain-meta">
            <span>Classroom {infrastructure.classroom_health ?? 0}%</span>
            <span>Toilet {infrastructure.toilet_functional ?? 0}%</span>
          </div>
        </article>

        <article className="ed-domain-card" onClick={() => onDomainClick?.("teacher")} onKeyDown={(e) => handleDomainKeyDown(e, "teacher")} role="button" tabIndex={0}>
          <div className="ed-domain-head">
            <div className="ed-icon-box purple"><IconGraduation /></div>
            <span className={`ed-status-dot ${dotClass(teacher.index)}`} />
          </div>
          <h3>Teacher Overview</h3>
          <div className="ed-domain-score">
            <strong>{teacher.index ?? 0}</strong>
            <span>Domain Health</span>
          </div>
          <div className="ed-progress-line">
            <i style={{ width: `${teacher.index ?? 0}%`, background: "var(--ed-purple)" }} />
          </div>
          <div className="ed-domain-meta">
            <span>Teachers {fmt(teacher.total_teachers)}</span>
            <span>CTET {teacher.ctet_pct ?? 0}%</span>
          </div>
        </article>

        <article className="ed-domain-card" onClick={() => onDomainClick?.("operational")} onKeyDown={(e) => handleDomainKeyDown(e, "operational")} role="button" tabIndex={0}>
          <div className="ed-domain-head">
            <div className="ed-icon-box orange"><IconOperational /></div>
            <span className={`ed-status-dot ${dotClass(operational.index)}`} />
          </div>
          <h3>Operational Overview</h3>
          <div className="ed-domain-score">
            <strong>{operational.index ?? 0}</strong>
            <span>Domain Health</span>
          </div>
          <div className="ed-progress-line">
            <i style={{ width: `${operational.index ?? 0}%`, background: "var(--ed-secondary)" }} />
          </div>
          <div className="ed-domain-meta">
            <span>Completion {operational.completion_rate ?? 0}%</span>
            <span>Certified {operational.certification_rate ?? 0}%</span>
          </div>
        </article>
      </section>

      <div className="ed-section-title">
        <div>
          <h2>Performance Diagnostics</h2>
          <p>Chart area separated from KPI cards for a clearer first fold and better chart width.</p>
        </div>
        <button type="button" className="ed-section-action" onClick={onScrollToRankings}>
          Go to rankings →
        </button>
      </div>

      <section className="ed-analytics-grid">
        <article className="ed-panel">
          <div className="ed-panel-head">
            <div>
              <h3>OSH Domain Contribution</h3>
              <p>Contribution view clarifies what is driving the composite school health score.</p>
            </div>
            <span className="ed-pill">Score weight</span>
          </div>
          <div className="ed-bar-list" aria-label="OSH domain contribution bar chart">
            {breakdown.map((row) => {
              const barClass =
                row.domain === "Infrastructure"
                  ? "green"
                  : row.domain === "Teacher Quality"
                    ? "purple"
                    : row.domain === "Operational"
                      ? "orange"
                      : "";
              const widthPct = Math.round(((row.contribution || 0) / maxContribution) * 100);
              const label =
                row.domain === "Student Identity"
                  ? <>Student<br />Overview</>
                  : row.domain === "Infrastructure"
                    ? <>Infrastructure<br />Overview</>
                  : row.domain === "Teacher Quality"
                    ? <>Teacher<br />Overview</>
                    : row.domain === "Operational"
                      ? <>Operational<br />Overview</>
                      : row.domain;
              return (
                <div className="ed-hbar-row" key={row.domain}>
                  <div className="ed-hbar-label">{label}</div>
                  <div className="ed-hbar-track">
                    <div className={`ed-hbar ${barClass}`} style={{ width: `${Math.max(widthPct, 8)}%` }} />
                  </div>
                  <div className="ed-hbar-value">{row.contribution ?? 0}</div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="ed-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Domain Performance Radar</h3>
              <p>Balanced view across core domains.</p>
            </div>
            <span className="ed-pill">Index</span>
          </div>
          <div className="ed-radar-wrap">
            <svg viewBox="0 0 360 340" role="img" aria-label="Radar chart of domain performance">
              <g transform="translate(180 170)">
                <polygon points="0,-130 130,0 0,130 -130,0" fill="none" stroke="#d7e3f6" strokeWidth="1.5" />
                <polygon points="0,-98 98,0 0,98 -98,0" fill="none" stroke="#d7e3f6" strokeWidth="1.5" />
                <polygon points="0,-66 66,0 0,66 -66,0" fill="none" stroke="#d7e3f6" strokeWidth="1.5" />
                <polygon points="0,-34 34,0 0,34 -34,0" fill="none" stroke="#d7e3f6" strokeWidth="1.5" />
                <line x1="0" y1="-130" x2="0" y2="130" stroke="#d7e3f6" />
                <line x1="-130" y1="0" x2="130" y2="0" stroke="#d7e3f6" />
                <polygon points={radarPolygon} fill="rgba(37,99,235,.34)" stroke="#2563eb" strokeWidth="3" />
                <text x="0" y="-150" textAnchor="middle" className="ed-radar-label">Identity</text>
                <text x="148" y="5" textAnchor="start" className="ed-radar-label">Infrastructure</text>
                <text x="0" y="155" textAnchor="middle" className="ed-radar-label">Teacher</text>
                <text x="-148" y="5" textAnchor="end" className="ed-radar-label">Operational</text>
                <text x="18" y="-126" className="ed-radar-num">{Math.round(radarScores.identity)}</text>
                <text x="114" y="-12" className="ed-radar-num">{Math.round(radarScores.infrastructure)}</text>
                <text x="10" y="55" className="ed-radar-num">{Math.round(radarScores.teacher)}</text>
                <text x="-92" y="-10" className="ed-radar-num">{Math.round(radarScores.operational)}</text>
              </g>
            </svg>
          </div>
        </article>
      </section>

      <div className="ed-section-title" id="ed-block-rankings">
        <div>
          <h2>Block Ranking and Recommended Actions</h2>
          <p>Rankings and actions are separated from the main KPIs so users can scan, diagnose, and then act.</p>
        </div>
        <button
          type="button"
          className="ed-section-action"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          Back to top ↑
        </button>
      </div>

      <section className="ed-two-column">
        <article className="ed-panel ed-table-card">
          <div className="ed-table-head">
            <div>
              <h3>Block-Wise OSH Rankings</h3>
              <p>Top performing blocks by composite score.</p>
            </div>
            <span className="ed-pill">Top 20</span>
          </div>
          <table className="ed-table" aria-label="Block-wise OSH rankings">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Block</th>
                <th>OSH Score</th>
                <th>Identity</th>
                <th>Infra</th>
                <th>Teacher</th>
                <th>Ops</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {blockRankings.length > 0 ? (
                blockRankings.map((block) => (
                  <tr key={block.block_name || block.rank}>
                    <td>{block.rank}</td>
                    <td>
                      <BlockLink blockCode={block.block_code} blockName={block.block_name}>
                        {block.block_name}
                      </BlockLink>
                    </td>
                    <td className="ed-score">{block.shi_score}</td>
                    <td>{block.identity_score}</td>
                    <td>{block.infra_score}</td>
                    <td>{block.teacher_score}</td>
                    <td>{block.ops_score}</td>
                    <td>
                      <span className={`ed-tag ${tagClass(block.rag?.status)}`}>{block.rag?.status ?? "Amber"}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8}>No block ranking data available for the selected scope.</td>
                </tr>
              )}
            </tbody>
          </table>
        </article>

        <article className="ed-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Three focused actions for executive review.</p>
            </div>
            <span className="ed-pill">Priority</span>
          </div>
          <div className="ed-action-card">
            {actions.map((action) => (
              <div className="ed-action-item" key={action.title}>
                <div className={`ed-icon-box ${action.iconVariant}`}>{actionIcon(action.icon)}</div>
                <div>
                  <h4>{action.title}</h4>
                  <p>{action.description}</p>
                </div>
              </div>
            ))}
            <div className="ed-action-footer">
              <b>Next review recommendation</b>
              <span>
                Review teacher-quality and certification progress weekly until the amber gap reduces by at least 10 points.
              </span>
            </div>
          </div>
        </article>
      </section>

      <ExecutivePriorityDomainsModal
        open={priorityDomainsOpen}
        onClose={() => setPriorityDomainsOpen(false)}
        domains={domains}
        blockRankings={blockRankings}
        scopeLabel={scopeLabel}
        shiValue={shiValue}
        ragStatus={ragStatus}
        onDomainNavigate={onDomainClick}
      />
    </div>
  );
};

export default ExecutiveOverviewTab;
